import { FormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Revisione53State } from '../../../../store/revisione53.reducers';
import { getControl, getFormArray, getGroup } from '@shared/helpers/form.helper';
import { formPaths } from '@shared/constants/form-paths.const';
import { MyVerbale } from '../../../../models/interfaces/revisione.interface';
import { cloneDeep } from 'lodash-es';
import { updateVerbaleOnStore } from '@core/helpers/create-validator.helper';
import { toNum } from '@shared/helpers/math.helper';
import { calcolaScadenzaOrganoAmministrativo } from '../../common/helpers/norme-store-update.helper';
import { withChangeDebounced } from '@shared/operators/with-change-debounced.operator';
import { debounceTime, merge, startWith } from 'rxjs';
import { Utilities } from '@core/helpers/utilities';
import { getNormeSrlControl, getNormeSrlGroup } from '../validators/helpers/norme-srl-form-validators.helper';

export const setNormeSrlSpecificListeners = (form: FormGroup, store: Store<Revisione53State>): void => {
    my_SRLAmmUnicoSocio(form, store);
    my_CkSrlSindaciRevisori(form, store);
    my_SrlScadenzaAmministratori(form, store);
    my_CalcoloSRL20(form, store);
};

const my_SRLAmmUnicoSocio = (form: FormGroup, store: Store<Revisione53State>): void => {
    const allAmminUnico = getFormArray(form, `${formPaths.ammControllo}.my_AllAmminUnico`);
    allAmminUnico.controls.forEach((group: FormGroup) => {
        const control = getControl(group, 'my_SRLAmmUnicoSocio');
        control.valueChanges.pipe(withChangeDebounced(control)).subscribe((value) => {
            if (value === '0') {
                control.patchValue('1', { emitEvent: false });
                updateVerbaleOnStore(store, (myVerbale) => {
                    const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
                    myVerbaleClone.my_SezioneI__Rilevazione.my_AmministrazioneControllo.my_AllAmminUnico = allAmminUnico.value;
                    return myVerbaleClone;
                });
            }
        });
    });
};

const my_CkSrlSindaciRevisori = (form: FormGroup, store: Store<Revisione53State>): void => {
    const normeSrlGroup = getNormeSrlGroup(form);
    const ckSindaciRevisoriControl = getControl(normeSrlGroup, 'my_CkSrlSindaciRevisori');
    const ckSindaciSceltiCoerenzaControl = getControl(normeSrlGroup, 'my_CkSrlSindaciSceltiCoerenza');
    ckSindaciRevisoriControl.valueChanges.pipe(withChangeDebounced(ckSindaciRevisoriControl)).subscribe((value) => {
        const newValue = value === '1' ? '1' : '';
        ckSindaciSceltiCoerenzaControl.patchValue(newValue, { emitEvent: false });
        updateVerbaleOnStore(store, (myVerbale) => {
            const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
            myVerbaleClone.my_SezioneI__Rilevazione.my_AmministrazioneControllo.my_NormeSrl.my_CkSrlSindaciSceltiCoerenza = newValue;
            return myVerbaleClone;
        });
    });
};

const my_SrlScadenzaAmministratori = (form: FormGroup, store: Store<Revisione53State>): void => {
    const control = getNormeSrlControl(form, 'my_SrlScadenzaAmministratori');
    control.valueChanges.pipe(withChangeDebounced(control)).subscribe(() => {
        calcolaScadenzaOrganoAmministrativo(form, store);
    });
};

const my_CalcoloSRL20 = (form: FormGroup, store: Store<Revisione53State>): void => {
    const myBilancioGroup = getGroup(form, formPaths.bilancio);
    const myDatiBilancioGroup = getGroup(form, formPaths.datiBilancio);
    const baseSocialeGroup = getGroup(form, formPaths.baseSociale);
    const my_CalcoloSRL20Control = getNormeSrlControl(form, 'my_CalcoloSRL20');
    const my_CapSocSottoscrSecondoEsControl = getControl(myBilancioGroup, 'my_CapSocSottoscrSecondoEs');
    const my_UnitaSociUtilControl = getControl(baseSocialeGroup, 'my_UnitaSociUtil');
    const my_UnitaNonSociUtilControl = getControl(baseSocialeGroup, 'my_UnitaNonSociUtil');
    const my_StPaAtTOT2Control = getControl(myDatiBilancioGroup, 'my_StatoPatrimoniale__Attivo.my_StPaAtTOT2');
    const my_CoEcA12Control = getControl(myDatiBilancioGroup, 'my_ContoEconomico.my_CoEcA12');

    merge(
        my_CapSocSottoscrSecondoEsControl.valueChanges.pipe(startWith(my_CapSocSottoscrSecondoEsControl.value)),
        my_UnitaSociUtilControl.valueChanges.pipe(startWith(my_UnitaSociUtilControl.value)),
        my_UnitaNonSociUtilControl.valueChanges.pipe(startWith(my_UnitaNonSociUtilControl.value)),
        my_StPaAtTOT2Control.valueChanges.pipe(startWith(my_StPaAtTOT2Control.value)),
        my_CoEcA12Control.valueChanges.pipe(startWith(my_CoEcA12Control.value))
    )
        .pipe(debounceTime(Utilities.LISTENERS_DEBOUNCE_TIME))
        .subscribe(() => {
            const capSoc = toNum(my_CapSocSottoscrSecondoEsControl.value);
            const unitaSoci = toNum(my_UnitaSociUtilControl.value);
            const unitaNonSoci = toNum(my_UnitaNonSociUtilControl.value);
            const stPaAtTOT2 = toNum(my_StPaAtTOT2Control.value);
            const coEcA12 = toNum(my_CoEcA12Control.value);
            const units = unitaSoci + unitaNonSoci;
            const result = String(
                capSoc >= 999_999_999_999 ||
                    (capSoc < 120_000 && ((units > 50 && stPaAtTOT2 > 4_400_000) || (units > 50 && coEcA12 > 8_800_000) || (stPaAtTOT2 > 4_400_000 && coEcA12 > 8_800_000)))
            )?.toLowerCase();
            my_CalcoloSRL20Control.setValue(result, { emitEvent: false });
            updateVerbaleOnStore(store, (v) => {
                const myVerbale: MyVerbale = cloneDeep(v);
                myVerbale.my_SezioneI__Rilevazione.my_AmministrazioneControllo.my_NormeSrl.my_CalcoloSRL20 = result;
                return myVerbale;
            });
        });
};
